import { KatapultJob } from '../../../modules/katapult-job/KatapultJob.js';
import { onMapItemDragEnd } from './map-interaction-service.js';

/** @import { MapItemEvent } from '../map-interaction-types.js' */

/**
 * Writes a moved node's new location to its job.
 *
 * @param {string} jobId
 * @param {string} nodeId
 * @param {[number, number]} location - The new location as a latitude longitude pair.
 * @returns {Promise<void>}
 */
async function moveJobNode(jobId, nodeId, location) {
  // Reject malformed input before touching the job
  const [latitude, longitude] = location;
  if (!jobId || !nodeId) throw new Error('Cannot move a node without a jobId and a nodeId');
  if (latitude == null || longitude == null) throw new Error('Cannot move a node without a latitude and a longitude');

  // Set the node's new location on the job
  const katapultJobRef = KatapultJob.fromId(jobId);
  await katapultJobRef.setNodeLocation(nodeId, [latitude, longitude]);
}

/**
 * Persists a finished node drag to its job. This is the glue between two layers that do not know
 * about each other: the map interaction service publishes a generic drag end event, this writes the
 * moved node back to its job.
 *
 * @param {MapItemEvent} event
 */
function commitNodeMove(event) {
  // Only nodes have a location to move, ignore any other dragged entity
  const { mapItem, position } = event;
  if (mapItem?.type !== 'node') return;

  // Hand the new location to the job
  moveJobNode(mapItem.jobId, mapItem.nodeId, [position.latitude, position.longitude]).catch((error) => {
    console.error('Error persisting node move:', error);
  });
}

/** @type {?() => void} Teardown for the drag end subscription, null while off. */
let unsubscribeFromNodeDragEnd = null;

/**
 * Turns node move persistence on by subscribing to drag end events. These only ever fire for the
 * main map, so no further gating is needed. Safe to call repeatedly, only the first call subscribes.
 */
export function init() {
  if (unsubscribeFromNodeDragEnd != null) return;

  unsubscribeFromNodeDragEnd = onMapItemDragEnd(commitNodeMove);
}

/**
 * Turns node move persistence off by unsubscribing from drag end events. Safe to call repeatedly, it
 * does nothing while already off.
 */
export function teardown() {
  if (unsubscribeFromNodeDragEnd == null) return;

  unsubscribeFromNodeDragEnd();
  unsubscribeFromNodeDragEnd = null;
}
